import { lazy, Suspense, useEffect, useRef, useState, type ReactNode } from 'react';
import { NavLink, Route, Routes, useLocation } from 'react-router';
import { Skeleton, SkipLink } from '@repair-please/ui';
import { EnvironmentBanner } from './environment-banner.js';
import { AppErrorBoundary } from './error-boundary.js';
import { usePageAnnouncement } from './document-meta.js';
import { useOnline } from './use-online.js';
import { useLocale } from './locale.js';
import { locales, catalogs, type Locale } from './i18n/index.js';
import { Offline } from './routes/offline.js';
import { ServerError } from './routes/server-error.js';
import { NotFound } from './routes/not-found.js';

/**
 * The content routes are code-split (issue #377): each is a separate chunk
 * fetched on demand, with the shell, the offline page, the 500 page and the
 * not-found page deliberately eager. The fallback pages must not themselves
 * depend on a network fetch — see `offline.tsx`.
 */
const Landing = lazy(() => import('./routes/landing.js').then((m) => ({ default: m.Landing })));
const Audience = lazy(() => import('./routes/audience.js').then((m) => ({ default: m.Audience })));
const SignInPreview = lazy(() =>
  import('./routes/sign-in-preview.js').then((m) => ({ default: m.SignInPreview })),
);
const Dashboard = lazy(() =>
  import('./routes/dashboard.js').then((m) => ({ default: m.Dashboard })),
);
const KnowledgeIndexPage = lazy(() =>
  import('./routes/knowledge.js').then((m) => ({ default: m.KnowledgeIndexPage })),
);
const KnowledgeDetailPage = lazy(() =>
  import('./routes/knowledge.js').then((m) => ({ default: m.KnowledgeDetailPage })),
);

/**
 * The chunk behind each nav target, for hover/intent prefetch (issue #377).
 *
 * The `import()` calls are shared with the `lazy()` components above through
 * the module cache, so a prefetch that is followed by a click costs nothing
 * extra, and a prefetch dropped on the floor costs one chunk fetch.
 */
const ROUTE_IMPORTS: Readonly<Record<string, () => Promise<unknown>>> = {
  '/': () => import('./routes/landing.js'),
  '/tenant': () => import('./routes/audience.js'),
  '/advocate': () => import('./routes/audience.js'),
  '/landlord': () => import('./routes/audience.js'),
  '/signin': () => import('./routes/sign-in-preview.js'),
  '/dashboard': () => import('./routes/dashboard.js'),
  '/knowledge': () => import('./routes/knowledge.js'),
};

function prefetchRoute(to: string): void {
  void ROUTE_IMPORTS[to]?.();
}

const navClass = ({ isActive }: { isActive: boolean }): string =>
  isActive ? 'app-nav__link app-nav__link--active' : 'app-nav__link';

/**
 * A primary nav link that warms its route's chunk on hover or keyboard focus —
 * intent, not click, is the earliest reliable signal that a reader is heading
 * there. Both handlers are idempotent because the underlying `import()` is
 * cached by the module system.
 */
function NavItem({ to, children }: { readonly to: string; readonly children: ReactNode }) {
  return (
    <NavLink
      to={to}
      className={navClass}
      onMouseEnter={() => prefetchRoute(to)}
      onFocus={() => prefetchRoute(to)}
    >
      {children}
    </NavLink>
  );
}

function LocaleSwitcher() {
  const { locale, setLocale, t } = useLocale();
  return (
    <div className="app-locale" role="group" aria-label="Language">
      {locales.map((candidate: Locale) => (
        <button
          key={candidate}
          type="button"
          className="app-locale__option"
          aria-pressed={candidate === locale}
          lang={candidate === 'zh-hans' ? 'zh-Hans' : candidate}
          onClick={() => setLocale(candidate)}
        >
          {catalogs[candidate].localeName}
        </button>
      ))}
      <span className="rp-visually-hidden" aria-live="polite">
        {t.localeName}
      </span>
    </div>
  );
}

export function App() {
  const { t } = useLocale();
  const location = useLocation();
  const online = useOnline();
  const announcement = usePageAnnouncement();
  const mainRef = useRef<HTMLElement>(null);
  // The initial location KEY, not a "first effect run" counter: StrictMode
  // replays this effect on the first mount in development and a counter made
  // the replay look like a navigation, so the shell focused `<main>` and
  // scrolled on the initial load — the very behaviour this effect exists to
  // withhold (cross-review F003, round three).
  const [initialKey] = useState(location.key);
  // A ref here, not state: it is read only inside the effect, and it must
  // survive a POP back to the initial entry, whose key is restored and would
  // otherwise read as "still on the initial load".
  const navigatedOnce = useRef(false);

  useEffect(() => {
    if (location.key === initialKey && !navigatedOnce.current) return;
    navigatedOnce.current = true;
    mainRef.current?.focus();
    window.scrollTo(0, 0);
  }, [location.key, initialKey]);

  return (
    <div className="app-frame">
      <SkipLink targetId="main" label={t.a11y.skipToContent} />
      {/*
        The route-change announcement (issue #377). The live region is part of
        the shell and therefore already in the DOM when a route's meta effect
        sets the string, which is what a live region requires to be read.
      */}
      <span className="rp-visually-hidden" aria-live="polite">
        {announcement}
      </span>
      <div className="app-chrome">
        <EnvironmentBanner />
        <header className="app-header">
          <NavLink to="/" className="app-wordmark">
            {t.brand.wordmark}
          </NavLink>
          <nav className="app-nav" aria-label="Primary">
            <NavItem to="/tenant">{t.nav.tenants}</NavItem>
            <NavItem to="/advocate">{t.nav.advocates}</NavItem>
            <NavItem to="/landlord">{t.nav.landlords}</NavItem>
            <NavItem to="/dashboard">{t.nav.dashboard}</NavItem>
            <NavItem to="/knowledge">{t.nav.knowledge}</NavItem>
            <NavItem to="/signin">{t.nav.signIn}</NavItem>
          </nav>
          <LocaleSwitcher />
        </header>
      </div>
      <main id="main" ref={mainRef} tabIndex={-1} className="app-main">
        {online ? (
          /*
            Keyed by pathname so a route that throws is recovered by navigating
            away rather than pinning the 500 page onto every later route
            (issue #376). It sits INSIDE `<main>` so the chrome stays usable.
            Retry reloads the document, the only recovery that re-requests a
            `React.lazy` chunk whose import was rejected (cross-review F003).
          */
          <AppErrorBoundary
            key={location.pathname}
            fallback={() => <ServerError onRetry={() => window.location.reload()} />}
          >
            <Suspense fallback={<Skeleton label={t.a11y.loading} lines={5} />}>
              <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/tenant" element={<Audience audience="tenant" />} />
                <Route path="/advocate" element={<Audience audience="advocate" />} />
                <Route path="/landlord" element={<Audience audience="landlord" />} />
                <Route path="/signin" element={<SignInPreview />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/knowledge" element={<KnowledgeIndexPage />} />
                <Route path="/knowledge/:code" element={<KnowledgeDetailPage />} />
                <Route path="/offline" element={<Offline />} />
                <Route path="/500" element={<ServerError />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </AppErrorBoundary>
        ) : (
          <Offline />
        )}
      </main>
      <footer className="app-footer">
        <p className="app-footer__disclaimer">{t.footer.disclaimer}</p>
        <p className="app-footer__rights">{t.footer.rights}</p>
      </footer>
    </div>
  );
}
